import { pushSettings } from '@bb/contracts';
import { Button, Checkbox, Field, Input, RadioGroup, Select, Skeleton, Textarea } from '@bb/ui';
import { useRef } from 'react';
import type { IosCategoryView } from '../../../api/client.ts';
import { LocaleTabs } from '../shared/LocaleTabs.tsx';
import { PersonalizationButton } from '../shared/PersonalizationButton.tsx';
import type { ChannelContent, LocaleSettings } from '../shared/localization.ts';
import { ActionEditor } from './ActionEditor.tsx';
import { LengthCounter } from './LengthCounter.tsx';
import { FIELD_LABELS, type IssueLookup } from './pushChecks.ts';
import {
  MAX_ANDROID_BUTTONS,
  nextButtonId,
  PLATFORM_LABELS,
  setText,
  textAt,
  type PushAction,
  type PushButton,
  type PushButtonAction,
  type PushComposerMode,
  type PushContentInput,
  type PushPlatform,
} from './pushDraft.ts';
import { errorText, FieldIssueList } from './PushIssues.tsx';
import type { PreviewSurface } from './pushLength.ts';
import type { PushRegistry } from './usePushRegistry.ts';

export interface PushContentTabProps {
  environmentId: string;
  /** The campaign being edited: its trigger data is offered by the personalization buttons. */
  campaignId?: string;
  value: PushContentInput;
  onChange: (next: PushContentInput) => void;
  /** Platforms this content is written for: their overrides, buttons and category are shown. */
  platforms: readonly PushPlatform[];
  /** Shared content has per-platform overrides; a separate platform's content has its own texts. */
  mode: PushComposerMode;
  /** The environment's locales (BB-55); `undefined` while they load. */
  locales: LocaleSettings | undefined;
  registry: PushRegistry;
  /** Issues of the last check, by field path. */
  issues: IssueLookup;
  /** The notification state each platform's preview shows: the length counters call out its limit. */
  surfaces?: Partial<Record<PushPlatform, PreviewSurface>>;
  readOnly?: boolean;
  /** The last failed save or publish: a 422 `translation_incomplete` is shown in the locale tabs. */
  saveError?: unknown;
}

const TEXT_PATHS: Record<'common' | PushPlatform, readonly string[]> = {
  common: ['title', 'body'],
  android: ['androidOverrides.title', 'androidOverrides.body', 'androidOverrides.summary'],
  ios: ['iosOverrides.title', 'iosOverrides.subtitle', 'iosOverrides.body'],
};

/** Texts a translation covers: the filled-in ones of the content's platforms. */
export function translatableFields(content: PushContentInput, platforms: readonly PushPlatform[]): string[] {
  return [...TEXT_PATHS.common, ...platforms.flatMap((p) => TEXT_PATHS[p])].filter((path) => textAt(content, path).trim() !== '');
}

interface TextFieldProps {
  environmentId: string;
  campaignId?: string;
  content: PushContentInput;
  onChange: (next: PushContentInput) => void;
  path: string;
  label: string;
  hint?: string;
  placeholder?: string;
  multiline?: boolean;
  readOnly: boolean;
  issues: IssueLookup;
  counter?: { platforms: readonly PushPlatform[]; withImage: boolean; surfaces?: Partial<Record<PushPlatform, PreviewSurface>> };
}

/** A text with Liquid: the personalization button inserts variables at the cursor; long texts get a length counter. */
function TextField({ environmentId, campaignId, content, onChange, path, label, hint, placeholder, multiline = false, readOnly, issues, counter }: TextFieldProps) {
  const ref = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  const text = textAt(content, path);
  const found = issues(path);
  const set = (next: string) => onChange(setText(content, path, next));
  return (
    <div className="push-text-field">
      <div className="push-text-field__row">
        <Field label={label} hint={hint} error={errorText(found)}>
          {multiline ? (
            <Textarea ref={ref} rows={3} value={text} placeholder={placeholder} disabled={readOnly} onChange={(e) => set(e.target.value)} />
          ) : (
            <Input ref={ref} value={text} placeholder={placeholder} disabled={readOnly} onChange={(e) => set(e.target.value)} />
          )}
        </Field>
        <PersonalizationButton environmentId={environmentId} campaignId={campaignId} target={ref} value={text} onChange={set} label={`Personalize ${label.toLowerCase()}`} disabled={readOnly} />
      </div>
      {counter ? <LengthCounter text={text} platforms={counter.platforms} withImage={counter.withImage} selected={counter.surfaces} label={`${label} length`} /> : <LengthCounter text={text} label={`${label} length`} />}
      {/* Truncation is the length counter's: the server's «may be cut» warnings stay in the checks summary. */}
      <FieldIssueList issues={{ errors: [], warnings: counter ? found.warnings.filter((w) => w.code !== 'may_truncate') : found.warnings }} />
    </div>
  );
}

/** The buttons a category draws: a preset's or its own. */
export function categoryButtons(category: IosCategoryView): Array<{ id: string; title: string; opensApp: boolean }> {
  if (category.preset) return pushSettings.IOS_CATEGORY_PRESETS[category.preset].map((b) => ({ id: b.id, title: b.title, opensApp: b.opensApp }));
  return category.buttons.map((b) => ({ id: b.id, title: b.title, opensApp: b.opensApp }));
}

function AndroidButtons({ value, onChange, readOnly, issues }: { value: PushContentInput; onChange: (next: PushContentInput) => void; readOnly: boolean; issues: IssueLookup }) {
  const buttons = value.androidButtons ?? [];
  const write = (next: PushButton[]) => {
    const { androidButtons: _drop, ...rest } = value;
    onChange(next.length ? { ...rest, androidButtons: next } : rest);
  };
  const update = (i: number, patch: Partial<PushButton>) => write(buttons.map((b, n) => (n === i ? { ...b, ...patch } : b)));
  const listIssues = issues('androidButtons');
  return (
    <fieldset className="push-buttons">
      <legend>Android buttons</legend>
      {buttons.length === 0 && <p className="hint">No buttons: a tap opens the click action.</p>}
      {buttons.map((b, i) => {
        const labelIssues = issues(`androidButtons[${i}].label`);
        const idIssues = issues(`androidButtons[${i}].id`);
        return (
          <div key={i} className="push-button" role="group" aria-label={`Button ${i + 1}`}>
            <Field label={`Button ${i + 1} label`} error={errorText(labelIssues)}>
              <Input value={b.label} disabled={readOnly} onChange={(e) => update(i, { label: e.target.value })} />
            </Field>
            <Field label={`Button ${i + 1} ID`} hint="Reported with clicks: lowercase letters, digits and _." error={errorText(idIssues)}>
              <Input value={b.id} disabled={readOnly} spellCheck={false} onChange={(e) => update(i, { id: e.target.value })} />
            </Field>
            <ActionEditor label={`Button ${i + 1} action`} value={b.action} allowDismiss readOnly={readOnly} issues={issues} path={`androidButtons[${i}].action`} onChange={(action) => update(i, { action })} />
            {!readOnly && (
              <Button size="sm" variant="ghost" onClick={() => write(buttons.filter((_, n) => n !== i))}>
                Remove button {i + 1}
              </Button>
            )}
          </div>
        );
      })}
      {!readOnly && (
        <Button size="sm" disabled={buttons.length >= MAX_ANDROID_BUTTONS} onClick={() => write([...buttons, { id: nextButtonId(buttons), label: 'Open', action: { kind: 'open_app' } }])}>
          Add button
        </Button>
      )}
      {buttons.length >= MAX_ANDROID_BUTTONS && <p className="hint">Android shows up to {MAX_ANDROID_BUTTONS} buttons.</p>}
      <FieldIssueList issues={listIssues} />
    </fieldset>
  );
}

function IosCategoryPicker({ value, onChange, readOnly, issues, registry }: { value: PushContentInput; onChange: (next: PushContentInput) => void; readOnly: boolean; issues: IssueLookup; registry: PushRegistry }) {
  const categories = registry.categories.data ?? [];
  const selectedId = value.iosCategory?.categoryId ?? '';
  const active = categories.filter((c) => !c.archivedAt);
  const selected = categories.find((c) => c.categoryId === selectedId);
  const known = Boolean(selected && !selected.archivedAt);
  const options = [
    { value: '', label: 'No buttons' },
    ...active.map((c) => ({ value: c.categoryId, label: c.preset ? `${c.categoryId} (${categoryButtons(c).map((b) => b.title).join(' / ')})` : c.categoryId })),
    ...(selectedId && !known ? [{ value: selectedId, label: `${selectedId} (not in the registry)` }] : []),
  ];
  const choose = (categoryId: string) => {
    const { iosCategory: _drop, ...rest } = value;
    const category = categories.find((c) => c.categoryId === categoryId);
    if (!categoryId || !category) return onChange(rest);
    const buttonActions = Object.fromEntries(categoryButtons(category).map((b) => [b.id, b.opensApp ? { kind: 'open_app' as const } : { kind: 'dismiss' as const }]));
    onChange({ ...rest, iosCategory: { categoryId, buttonActions } });
  };
  const setAction = (buttonId: string, action: PushButtonAction) => value.iosCategory && onChange({ ...value, iosCategory: { ...value.iosCategory, buttonActions: { ...value.iosCategory.buttonActions, [buttonId]: action } } });
  return (
    <fieldset className="push-buttons">
      <legend>iOS buttons</legend>
      {registry.categories.isPending ? (
        <Skeleton label="Loading iOS categories" />
      ) : (
        <Field label="Notification category" hint="iOS draws the buttons of a category the app registered (Push settings); the push says what each button does." error={errorText(issues('iosCategory.categoryId'))}>
          <Select options={options} value={selectedId} disabled={readOnly} onChange={(e) => choose(e.target.value)} />
        </Field>
      )}
      {registry.categories.isError && <p className="notice notice--warning">The iOS categories could not be loaded.</p>}
      {selectedId && !known && !registry.categories.isPending && <p className="hint push-issue--warning">This category is not in the environment registry: apps that do not register it show no buttons.</p>}
      {selected &&
        value.iosCategory &&
        categoryButtons(selected).map((b) => (
          <ActionEditor
            key={b.id}
            label={`“${b.title}” button action`}
            value={value.iosCategory!.buttonActions[b.id] ?? (b.opensApp ? { kind: 'open_app' } : { kind: 'dismiss' })}
            allowDismiss
            readOnly={readOnly}
            issues={issues}
            path={`iosCategory.buttonActions.${b.id}`}
            onChange={(action) => setAction(b.id, action)}
          />
        ))}
    </fieldset>
  );
}

/**
 * The Compose tab of the push composer (BB-73): notification type, title and message with personalization and length
 * counters, per-platform overrides (shared content) or the platform's own extras (summary text, subtitle), the click
 * action with per-platform overrides, Android buttons and the iOS category, the image; translations of the texts in
 * locale tabs (BB-55). Issues of the last check appear under their fields.
 */
export function PushContentTab({ environmentId, campaignId, value, onChange, platforms, mode, locales, registry, issues, surfaces, readOnly = false, saveError }: PushContentTabProps) {
  const silent = value.notificationType === 'silent';
  const android = platforms.includes('android');
  const ios = platforms.includes('ios');
  const withImage = Boolean(value.media?.imageUrl || value.media?.androidImageAssetId || value.media?.iosImageAssetId);
  const text = { environmentId, campaignId, content: value, onChange, readOnly, issues };
  // The common message is measured on the platforms that show it (an override replaces it).
  const bodyPlatforms = platforms.filter((p) => !(p === 'android' ? value.androidOverrides?.body : value.iosOverrides?.body));
  const extrasCount = Object.keys(value.extras ?? {}).length;

  const setOverrideAction = (platform: PushPlatform, action: PushAction | undefined) => {
    const overrides = { ...(value.actionOverrides ?? {}) };
    if (action) overrides[platform] = action;
    else delete overrides[platform];
    const { actionOverrides: _drop, ...rest } = value;
    onChange(Object.keys(overrides).length ? { ...rest, actionOverrides: overrides } : rest);
  };

  const form = (
    <div className="stack-form push-content" role="group" aria-label="Content">
      <RadioGroup
        label="Notification type"
        options={[
          { value: 'standard', label: 'Standard push', disabled: readOnly },
          { value: 'silent', label: 'Silent push (wakes the app, shows nothing)', disabled: readOnly },
        ]}
        value={value.notificationType ?? 'standard'}
        onValueChange={(v) => onChange({ ...value, notificationType: v as 'standard' | 'silent' })}
      />
      {silent ? (
        <p className="hint">A silent push refreshes the app in the background; texts, buttons and the image are not sent.</p>
      ) : (
        <>
          <TextField {...text} path="title" label="Title" hint={ios ? 'iOS shows the title on one line.' : undefined} />
          <TextField {...text} path="body" label="Message" multiline counter={{ platforms: bodyPlatforms, withImage, surfaces }} />
          {mode === 'separate' && android && <TextField {...text} path="androidOverrides.summary" label="Summary text" hint="Android, optional: shown next to the app name when the notification is expanded." />}
          {mode === 'separate' && ios && <TextField {...text} path="iosOverrides.subtitle" label="Subtitle" hint="iOS, optional: a line under the title." />}
          {mode === 'shared' && android && (
            <fieldset className="push-overrides" aria-label="Android overrides">
              <legend>Android overrides</legend>
              <p className="hint">Empty: Android shows the common texts.</p>
              <TextField {...text} path="androidOverrides.title" label="Android title" placeholder={value.title ?? ''} />
              <TextField {...text} path="androidOverrides.body" label="Android message" multiline placeholder={value.body ?? ''} counter={{ platforms: ['android'], withImage, surfaces }} />
              <TextField {...text} path="androidOverrides.summary" label="Summary text" hint="Optional: shown next to the app name when the notification is expanded." />
            </fieldset>
          )}
          {mode === 'shared' && ios && (
            <fieldset className="push-overrides" aria-label="iOS overrides">
              <legend>iOS overrides</legend>
              <p className="hint">Empty: iOS shows the common texts.</p>
              <TextField {...text} path="iosOverrides.title" label="iOS title" placeholder={value.title ?? ''} />
              <TextField {...text} path="iosOverrides.subtitle" label="Subtitle" hint="Optional: a line under the title." />
              <TextField {...text} path="iosOverrides.body" label="iOS message" multiline placeholder={value.body ?? ''} counter={{ platforms: ['ios'], withImage, surfaces }} />
            </fieldset>
          )}
        </>
      )}
      <fieldset className="push-interactions">
        <legend>Interactions</legend>
        <ActionEditor label="On-click behavior" value={value.action ?? { kind: 'open_app' }} readOnly={readOnly} issues={issues} path="action" onChange={(action) => onChange({ ...value, action: action as PushAction })} />
        {mode === 'shared' &&
          platforms.length > 1 &&
          platforms.map((p) => {
            const override = value.actionOverrides?.[p];
            return (
              <div key={p} className="push-action-override">
                <Checkbox label={`Different click action on ${PLATFORM_LABELS[p]}`} checked={Boolean(override)} disabled={readOnly} onCheckedChange={(on) => setOverrideAction(p, on ? (value.action ?? { kind: 'open_app' }) : undefined)} />
                {override && <ActionEditor label={`${PLATFORM_LABELS[p]} click action`} value={override} readOnly={readOnly} issues={issues} path={`actionOverrides.${p}`} onChange={(action) => setOverrideAction(p, action as PushAction)} />}
              </div>
            );
          })}
      </fieldset>
      {!silent && android && <AndroidButtons value={value} onChange={onChange} readOnly={readOnly} issues={issues} />}
      {!silent && ios && <IosCategoryPicker value={value} onChange={onChange} readOnly={readOnly} issues={issues} registry={registry} />}
      {!silent && (
        <div className="push-text-field">
          <Field label="Image URL" hint="Optional: an https:// image; may use Liquid." error={errorText(issues('media.imageUrl'))}>
            <Input value={value.media?.imageUrl ?? ''} placeholder="https://" spellCheck={false} disabled={readOnly} onChange={(e) => onChange(setText(value, 'media.imageUrl', e.target.value))} />
          </Field>
          {ios && withImage && registry.imageExtensionAvailable === false && <p className="hint push-issue--warning">No iOS app declares a Notification Service Extension: iOS shows this push without the image.</p>}
        </div>
      )}
      <p className="hint">
        Key-value pairs for the app: {extrasCount ? `${extrasCount} set` : 'none'} — edit them in the Settings tab.
      </p>
    </div>
  );

  if (!locales) return <Skeleton label="Loading languages" height={120} />;
  return (
    <LocaleTabs
      locales={locales}
      value={value as ChannelContent}
      onChange={(next) => onChange(next as PushContentInput)}
      fields={translatableFields(value, platforms)}
      labels={FIELD_LABELS}
      renderDefault={() => form}
      readOnly={readOnly}
      saveError={saveError}
      label="Languages"
    />
  );
}
